import Pagination from 'components/Blocks/Pagination/Pagination';
import ProductsList, { ProductListTypes } from 'components/Blocks/Product/List/ProductsList';
import { mapListedProductConnectionType } from 'connectors/products/Products';
import { useDomainConfig } from 'context/DomainConfigProvider/DomainConfigProvider';
import { CategoryProductsQueryDocumentApi } from 'graphql/generated';
import { useProductsData } from 'helpers/loadMore/useProductsData';
import { getUrlWithoutGetParameters } from 'helpers/parsing/urlParsing';
import { useCurrentLoadMoreQuery } from 'helpers/queryParams/useCurrentLoadMoreQuery';
import { useCurrentPageQuery } from 'helpers/queryParams/useCurrentPageQuery';
import { useGtmCategoryProductListView } from 'hooks/gtm/useGtmCategoryProductListView';
import { useBroadcastChannel } from 'hooks/useBroadcastChannel';
import Trans from 'next-translate/Trans';
import useTranslation from 'next-translate/useTranslation';
import { useRouter } from 'next/router';
import React, { RefObject, useEffect, useMemo, useState } from 'react';
import { usePersistStore } from 'store/usePersistStore';
import { useSessionStore } from 'store/useSessionStore';
import { CategoryDetailType } from 'types/category';
import { getCategoryOrSeoCategoryGtmListName } from 'utils/Gtm/Gtm';

type CategoryDetailProps = {
    category: CategoryDetailType;
    paginationScrollTargetRef: RefObject<HTMLDivElement>;
};

export const categoryDetailContentMessageTwClass = 'p-12 text-center text-body [&>div:first-of-type]:mb-5';

export const CategoryDetailProductsWrapper: FC<CategoryDetailProps> = ({ category, paginationScrollTargetRef }) => {
    const { t } = useTranslation();
    const [shouldRefetch, setShouldRefetch] = useState(false);
    const { asPath } = useRouter();
    const currentPage = useCurrentPageQuery();
    const currentLoadMore = useCurrentLoadMoreQuery();
    const { currencyCode } = useDomainConfig();
    const totalCount = category.productConnection.totalCount;
    const authLoading = usePersistStore((s) => s.authLoading);

    const wasRedirectedToSeoCategory = useSessionStore((s) => s.wasRedirectedToSeoCategory);
    const setWasRedirectedToSeoCategory = useSessionStore((s) => s.setWasRedirectedToSeoCategory);
    const { products, areProductsFetching, isLoadingMoreProducts } = useProductsData(
        CategoryProductsQueryDocumentApi,
        totalCount,
        shouldRefetch,
        {
            shouldAbortFetchingProducts: wasRedirectedToSeoCategory,
            abortedFetchCallback: () => setWasRedirectedToSeoCategory(false),
            pause: authLoading !== 'not-loading',
        },
    );
    const categoryProducts = products ? mapListedProductConnectionType(products, currencyCode).products : [];
    const gtmListName = useMemo(() => getCategoryOrSeoCategoryGtmListName(category), [category]);
    useGtmCategoryProductListView(category, getUrlWithoutGetParameters(asPath), categoryProducts, areProductsFetching);

    useBroadcastChannel('orderCompleteBroadcast', () => {
        setShouldRefetch(true);
    });

    useEffect(() => {
        if (shouldRefetch) {
            setShouldRefetch(false);
        }
    }, [shouldRefetch]);

    return (
        <>
            {(categoryProducts.length !== 0 || areProductsFetching) && (
                <ProductsList
                    currentPage={currentPage}
                    gtmListName={gtmListName}
                    isLoadMore={currentLoadMore > 0}
                    isLoading={areProductsFetching || isLoadingMoreProducts}
                    products={categoryProducts}
                    type={ProductListTypes.Normal}
                />
            )}
            {categoryProducts.length !== 0 && (
                <Pagination
                    isWithLoadMore
                    isLoading={isLoadingMoreProducts}
                    paginationScrollTargetRef={paginationScrollTargetRef}
                    totalCount={totalCount}
                />
            )}
            {totalCount === 0 && !areProductsFetching && (
                <div className={categoryDetailContentMessageTwClass}>
                    <div>
                        <strong>{t('No results match the filter')}</strong>
                    </div>
                    <div>
                        <Trans components={{ 0: <br /> }} i18nKey="ProductsNoResults" />
                    </div>
                </div>
            )}
        </>
    );
};
